import {
  Card,
  ECardBorderRadiusSize,
  ECardCarouselSize,
  ECardMediaSize,
} from '@outdoorsyco/bonfire';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { FavoriteButton } from '@/components/common/FavoriteButton';
import lightBoxCSS from '@/components/route/listing/Gallery/GalleryList/GalleryList.module.css';
import Lightbox from '@/components/switchback/Lightbox';
import ResponsiveImage from '@/components/utility/ResponsiveImage/ResponsiveImage';
import { WishlistModalWrapper } from '@/components/wishlists/WishlistModal';
import { IFavorite } from '@/redux/modules/wishlists';
import { getUserFavorites } from '@/redux/selectors/favorites';
import { IWishlistingEventData } from '@/services/analytics/listings/types';
import { IRentalTile } from '@/utility/mapSearchResultToTile';

import GalleryItem from '../../listing/Gallery/GalleryList/GalleryItem';

interface IListingPhotosProps {
  rental: IRentalTile;
  wishlistingEventData?: IWishlistingEventData;
  enhanceImages?: boolean;
}
export const ListingPhotos = ({
  rental,
  wishlistingEventData,
  enhanceImages,
}: IListingPhotosProps) => {
  let images: string[] = [];
  const favorites = useSelector(getUserFavorites);

  const { title, photoUrl, id } = rental;

  if (Array.isArray(photoUrl)) {
    images = photoUrl;
  } else if (typeof photoUrl === 'string') {
    images = [photoUrl];
  }

  const [activeSlide, setActiveSlide] = useState<number | null>(null);
  const isFavorite =
    favorites && favorites.rentals.some((favorite: IFavorite) => favorite.item_id === Number(id));

  const favoriteToggleButton = (
    <WishlistModalWrapper
      isFavorite={isFavorite}
      item={rental}
      itemType="rental"
      wishlistingEventData={wishlistingEventData}
      TriggerComponent={({ isFavorite: isWishlisted, toggleFavorite }) => (
        <FavoriteButton isFavorite={isWishlisted} onClick={toggleFavorite} />
      )}
    />
  );

  return (
    <div>
      <Card
        borderRadiusSize={ECardBorderRadiusSize.Large}
        showHoverEffect={false}
        withBoxShadow={false}
        withTransparentBackground={true}
        aria-label={title}
        title={title}>
        {images.length > 1 && (
          <Card.Carousel
            content={images.map((url, index) => (
              <ResponsiveImage
                key={uuid()}
                src={url}
                cropMode="fill"
                role="presentation"
                sourceSizes={['landscape572']}
                className="object-cover object-center w-full h-full"
                onClick={() => setActiveSlide(index)}
                enhanceImage={enhanceImages}
              />
            ))}
            size={ECardCarouselSize.Large}
            fullRadius
            className="min-h-[256px]">
            <div className="absolute top-0 left-0 flex items-start justify-end w-full p-2 gap-2">
              {favoriteToggleButton}
            </div>
          </Card.Carousel>
        )}
        {images.length <= 1 && (
          <Card.Media size={ECardMediaSize.Medium} fullRadius className="bg-gray-200 min-h-[256px]">
            {images.map((url, index) => (
              <ResponsiveImage
                key={uuid()}
                src={url}
                cropMode="fill"
                role="presentation"
                sourceSizes={['landscape572']}
                className="object-cover object-center w-full h-full"
                onClick={() => setActiveSlide(index)}
              />
            ))}
            <div className="absolute top-0 left-0 flex items-start justify-end w-full p-2 gap-2">
              {favoriteToggleButton}
            </div>
          </Card.Media>
        )}
      </Card>

      <div className={lightBoxCSS.wrapper}>
        <Lightbox
          infinite
          dots={false}
          adaptiveHeight
          lazyLoad="ondemand"
          customModalClass={lightBoxCSS.lightBoxModal}
          className={lightBoxCSS.lightBox}
          open={activeSlide !== null}
          initialSlide={activeSlide ?? 0}
          activeSlide={activeSlide ?? 0}
          onClose={() => setActiveSlide(null)}>
          {images.map((image, index) => (
            <GalleryItem image={image} isVideo={false} index={index} key={uuid()} />
          ))}
        </Lightbox>
      </div>
    </div>
  );
};
